import { supabase } from './supabase';

export interface InvoiceLineItem {
  id: string;
  productName: string;
  customName: string;
  category: string; // Primary | Package | Extra | Upsell | ''
  parentLineItemId: string | null;
  quantity: number;
  isAreaCalculation: boolean;
  unitPrice: number;
  totalPrice: number;
  total: number;
  totalInclBtw: number;
  totalInclBtwWithExtras: number;
  btw: number;
  btwAmount: number;
  discount: number;
  discountAmount: number;
  itemNumber: string;
  productId: string;
}

export interface InvoiceLineItemGroup {
  primary: InvoiceLineItem;
  packages: InvoiceLineItem[];
  extras: InvoiceLineItem[];
}

export interface TransactieRecord {
  id: string;
  amount: number;
  method: string; // Mollie | Pin | Contant | Bankrekening | iDEAL
  receivedAt: string;
  notes: string;
  checkedAfgestort: boolean;
  receivedByUserId: string;
  receivedByUserName: string;
  receivedByEmployeeId: string;
  receivedByEmployeeName: string;
  workOrderId: string | null;
  workOrderRef: string;
  invoiceId: string | null;
  invoiceRef: string;
  createdDate: string;
}

export interface InvoiceDetail {
  invoice: {
    id: string;
    name: string;
    accountId: string;
    accountName: string;
    workOrderId: string;
    workOrderRef: string;
    workOrderBrand: string;
    invoiceDate: string;
    dueDate: string;
    totals: {
      totalInclBtw: number;
      totalExclBtw: number;
      btw: number;
      subtotaal: number;
      discount: number;
    };
    status: {
      openAmount: number;
      transactionTotal: number;
      paid: boolean;
    };
    address: {
      nameOnInvoice: string;
      nameOverride: string;
      tav: string;
      formatted: string;
      formattedOverride: string;
      emailOverride: string;
    };
    mollie: {
      paymentUrl: string;
      paymentId: string;
      paymentStatus: string;
      qrCode: string;
      /**
       * Payment-id waarop een refund daadwerkelijk kan landen: factuurveld met
       * werkorder-fallback, gelijk aan wat de refund-service resolvet. Gebruik
       * dit voor refund-gates, niet `paymentId` (dat is bij werkorder-facturen
       * vrijwel altijd leeg).
       */
      refundPaymentId: string;
    };
    payment: {
      method: string;
      term: string;
    };
    contact: {
      id: string;
      name: string;
    };
    credit: {
      isCreditfactuur: boolean;
      creditedInvoiceId: string | null;
      creditedInvoiceName: string | null;
      creditInvoices: CreditInvoiceSummary[];
    };
    email: string;
    notes: string;
    /** Interne opvolg-notitie — alleen kantoor, NIET op de factuur. */
    internalNote: string;
    /** Door de klant toegezegde betaaldatum (yyyy-MM-dd) of '' als leeg. */
    promisedPaymentDate: string;
    createdDate: string;
    lastModifiedDate: string;
  };
  lineItems: InvoiceLineItem[];
  transacties: TransactieRecord[];
}

export interface CreditInvoiceSummary {
  id: string;
  name: string;
  totalInclBtw: number;
  openAmount: number;
  invoiceDate: string;
  notes: string;
  createdDate: string;
}

export interface InvoiceListItem {
  id: string;
  name: string;
  invoiceDate: string;
  dueDate: string;
  totalInclBtw: number;
  openAmount: number;
  transactionTotal: number;
  paid: boolean;
  isCreditfactuur: boolean;
  paymentMethod: string;
  accountId: string;
  customerName: string;
  postcode: string;
  city: string;
  workOrderId: string;
  workOrderRef: string;
  brand: string;
  hasMollie: boolean;
  createdDate: string;
  lastModifiedDate: string;
}

export type InvoiceListStatus = 'all' | 'open' | 'partial' | 'paid';

export type IncassoFase =
  | 'vervallen'
  | 'herinnering1'
  | 'aanmaning'
  | 'op-factuur'
  | 'kandidaat'
  | 'uitgesloten'
  | 'bij-incasso'
  | 'betaald'
  | 'oninbaar'
  | 'afgeboekt';

export interface VervallenListItem extends InvoiceListItem {
  daysOpen: number;
  fase: IncassoFase;
  incassoStatus: string | null;
  uitsluitReden: string | null;
  kandidaatSinds: string | null;
  overgedragenAt: string | null;
  opFactuur: boolean;
  toegezegdBetaaldatum: string | null;
}

export type VervallenScope = 'vervallen' | 'incasso';

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

export async function fetchInvoiceList(status: InvoiceListStatus = 'all'): Promise<InvoiceListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(`/api/invoices/list?status=${status}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Kon facturen niet laden');
  }
  const payload = (await response.json()) as { invoices?: InvoiceListItem[] };
  return Array.isArray(payload.invoices) ? payload.invoices : [];
}

export async function fetchVervallenList(scope: VervallenScope): Promise<VervallenListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(`/api/invoices/vervallen?scope=${scope}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Kon vervallen facturen niet laden');
  }
  const payload = (await response.json()) as { invoices?: VervallenListItem[] };
  return Array.isArray(payload.invoices) ? payload.invoices : [];
}

export type IncassoActie = 'uitsluiten' | 'terug-kandidaat' | 'al-bij-bos' | 'afboeken' | 'oninbaar';

/** Leesbare tekst voor een uitsluitreden uit incasso_dossiers (selector zet 'auto:…'). */
export function omschrijfUitsluitReden(reden: string | null | undefined): string | null {
  if (!reden) return null;
  const VAST: Record<string, string> = {
    'auto:klacht-open': 'open klacht op de opdracht',
    'auto:betaaltoezegging': 'betaaltoezegging in de toekomst',
    'auto:onbereikbaar:geen-adres': 'geen e-mailadres bekend; factuur niet aangekomen',
    'auto:onbereikbaar:bounce': 'e-mailadres komt terug (bounce); factuur en aanmaning niet aangekomen. Corrigeer het adres en stuur de factuur opnieuw',
    'auto:onbereikbaar:geen-factuurmail': 'geen gelogde factuurmail; stuur de factuur (opnieuw) vóór incasso',
    'auto:onbereikbaar:geen-aanmaning': 'geen gelogde aanmaning (dag 15); stuur de aanmaning vóór incasso',
  };
  return VAST[reden] ?? reden;
}

export async function postIncassoActie(
  invoiceId: string,
  actie: IncassoActie,
  reden?: string
): Promise<{ ok: boolean; status: string }> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/incasso-actie', {
    method: 'POST',
    headers,
    body: JSON.stringify({ invoiceId, actie, reden }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Incasso-actie mislukt');
  return payload as { ok: boolean; status: string };
}

export interface IncassoDossierPreview {
  dossier: {
    factuur: { nummer: string; openstaand: number; vervaldatum: string | null };
    schuldenaar: { naam: string; email: string | null; telefoon: string | null };
    herkomst: { omschrijving: string };
    tijdlijn: Array<{ op: string; kanaal: string; omschrijving: string; status: string; reconstructie?: boolean }>;
    waarschuwingen: string[];
    /** Poort: `grond` gevuld = de overdracht weigert (409) met `omschrijving`. */
    bereikbaarheid?: { adres: string | null; grond: string | null; omschrijving: string | null };
  };
  mail: { aan: string; van: string; replyTo: string; cc: string; subject: string; text: string };
  bijlage: string | null;
  bijlagen: string[];
  bijlageFout: string | null;
  incassoStatus: string;
}

export async function fetchIncassoDossierPreview(
  invoiceId: string,
  toelichting?: string
): Promise<IncassoDossierPreview> {
  const headers = await authHeaders();
  const params = new URLSearchParams({ id: invoiceId });
  if (toelichting?.trim()) params.set('toelichting', toelichting.trim());
  const response = await fetch(`/api/invoices/incasso-dossier?${params}`, { headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Kon dossier niet opbouwen');
  return payload as IncassoDossierPreview;
}

export async function postIncassoOverdracht(
  invoiceId: string,
  toelichting?: string
): Promise<{ ok: boolean; ref: string }> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/incasso-overdracht', {
    method: 'POST',
    headers,
    body: JSON.stringify({ invoiceId, toelichting: toelichting?.trim() || undefined }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Overdracht mislukt');
  return payload as { ok: boolean; ref: string };
}

export async function searchInvoices(
  query: string,
  signal?: AbortSignal
): Promise<InvoiceListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(`/api/invoices/search?q=${encodeURIComponent(query)}`, {
    headers,
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Zoeken mislukt');
  }
  const payload = (await response.json()) as { invoices?: InvoiceListItem[] };
  return Array.isArray(payload.invoices) ? payload.invoices : [];
}

/**
 * Alle openstaande facturen van één klant, oudste eerst. De klant wordt afgeleid
 * uit een factuur (`relatedInvoiceId`) of direct meegegeven (`accountId`) — de
 * bank-koppeldialog kent alleen de eerste.
 */
export async function fetchOpenInvoicesForCustomer(
  ref: { relatedInvoiceId: string } | { accountId: string },
  signal?: AbortSignal
): Promise<InvoiceListItem[]> {
  const headers = await authHeaders();
  const param =
    'accountId' in ref
      ? `accountId=${encodeURIComponent(ref.accountId)}`
      : `relatedInvoiceId=${encodeURIComponent(ref.relatedInvoiceId)}`;
  const response = await fetch(`/api/invoices/search?${param}&openOnly=1`, { headers, signal });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Openstaande facturen ophalen mislukt');
  }
  const payload = (await response.json()) as { invoices?: InvoiceListItem[] };
  return Array.isArray(payload.invoices) ? payload.invoices : [];
}

export async function fetchInvoiceDetail(id: string): Promise<InvoiceDetail> {
  const headers = await authHeaders();
  const response = await fetch(`/api/invoices/detail?id=${encodeURIComponent(id)}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(err.error || 'Request failed') as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return response.json();
}

export type UpdateInvoiceInternalNoteResult = { ok: true } | { ok: false; error: string };

/**
 * Zet de interne notitie + toegezegde betaaldatum op een factuur. Beide velden
 * zijn alleen voor kantoor en verschijnen NIET op de factuur of in
 * klant-communicatie. Lege waarde wist het veld (Apex coerce → null).
 * Bron='Office-App' wordt server-side gezet.
 */
export async function updateInvoiceInternalNote(
  invoiceId: string,
  fields: { internalNote: string; promisedPaymentDate: string }
): Promise<UpdateInvoiceInternalNoteResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/update', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      invoiceId,
      patch: {
        Interne_Notitie__c: fields.internalNote.trim() || null,
        Toegezegd_Betaaldatum__c: fields.promisedPaymentDate || null,
      },
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Opslaan mislukt' }));
    return { ok: false, error: err.error || 'Opslaan mislukt' };
  }
  return { ok: true };
}

/**
 * Zet alleen de toegezegde betaaldatum op een factuur (date-only quick-save).
 * Een toekomstige datum onderdrukt de automatische betalingsherinneringen (SF).
 * Lege waarde ('' of null) wist het veld en hervat de cadence. Raakt de interne
 * notitie niet aan.
 */
export async function setInvoicePromisedPaymentDate(
  invoiceId: string,
  date: string | null
): Promise<UpdateInvoiceInternalNoteResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/update', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      invoiceId,
      patch: { Toegezegd_Betaaldatum__c: date || null },
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Opslaan mislukt' }));
    return { ok: false, error: err.error || 'Opslaan mislukt' };
  }
  return { ok: true };
}

/**
 * Groepeer line-items volgens S11 template-logica:
 * - primaries: Product_Category__c === 'Primary' of null zonder parent. Per
 *   primary horen `packages` (Product_Category__c === 'Package' met parentId →
 *   primary.Id) en `extras` (category === 'Extra' met parentId → primary.Id).
 * - upsells: alle items met category === 'Upsell'.
 * - orphans: Extra/Package-items zonder bekende parent (defensief).
 */
export function groupLineItems(items: InvoiceLineItem[]): {
  primaries: InvoiceLineItemGroup[];
  upsells: InvoiceLineItem[];
  orphans: InvoiceLineItem[];
} {
  const byId = new Map<string, InvoiceLineItem>();
  for (const item of items) byId.set(item.id, item);

  const primaries: InvoiceLineItemGroup[] = [];
  const upsells: InvoiceLineItem[] = [];
  const orphans: InvoiceLineItem[] = [];

  // Eerste pass: identificeer primaries
  const primaryItems = items.filter(
    (i) =>
      (i.category === 'Primary' || i.category === '') &&
      i.parentLineItemId === null
  );

  for (const primary of primaryItems) {
    const packages = items.filter(
      (i) => i.category === 'Package' && i.parentLineItemId === primary.id
    );
    const extras = items.filter(
      (i) => i.category === 'Extra' && i.parentLineItemId === primary.id
    );
    primaries.push({ primary, packages, extras });
  }

  for (const item of items) {
    if (item.category === 'Upsell') {
      upsells.push(item);
      continue;
    }
    if (
      (item.category === 'Extra' || item.category === 'Package') &&
      (!item.parentLineItemId || !byId.has(item.parentLineItemId))
    ) {
      orphans.push(item);
    }
  }

  return { primaries, upsells, orphans };
}

export interface CreateTransactieBody {
  parentType: 'Werk_Order__c' | 'Invoice__c';
  parentId: string;
  bedrag: number;
  methode: 'Bankrekening' | 'Pin' | 'Contant' | 'iDEAL' | 'Mollie';
  datum: string;
  opmerking?: string;
}

export type CreateTransactieResult =
  | { ok: true; transactieId: string; parentType: string; parentId: string }
  | { ok: false; error: string };

export async function createTransactie(
  body: CreateTransactieBody
): Promise<CreateTransactieResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/transacties/create', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Aanmaken mislukt' }));
    return { ok: false, error: err.error || 'Aanmaken mislukt' };
  }
  const payload = await response.json();
  return {
    ok: true,
    transactieId: payload.transactieId,
    parentType: payload.parentType,
    parentId: payload.parentId,
  };
}

export type CreateInvoiceResult =
  | {
      ok: true;
      invoiceId: string;
      invoiceNaam: string;
      lineItemsCreated: number;
    }
  | {
      ok: false;
      error: string;
      code?: string;
      existingInvoiceId?: string | null;
      existingInvoiceNaam?: string | null;
    };

/**
 * Roept POST /api/invoices/create-from-werkorder aan.
 * Bron='Office-App' wordt server-side gezet.
 */
export async function createInvoiceFromWerkOrder(
  werkOrderId: string
): Promise<CreateInvoiceResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/create-from-werkorder', {
    method: 'POST',
    headers,
    body: JSON.stringify({ werkOrderId }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      error: payload.error || 'Factuur aanmaken mislukt',
      code: payload.code,
      existingInvoiceId: payload.existingInvoiceId ?? null,
      existingInvoiceNaam: payload.existingInvoiceNaam ?? null,
    };
  }
  return {
    ok: true,
    invoiceId: payload.invoiceId,
    invoiceNaam: payload.invoiceNaam,
    lineItemsCreated: payload.lineItemsCreated ?? 0,
  };
}

// ─── Vrijgeven voor herfacturatie ────────────────────────────────────────────

export type ReleaseWerkOrderResult =
  | {
      ok: true;
      werkOrderNummer: string | null;
      vorigeFactuurNummer: string | null;
      verrekeningBalansGezet: number;
      betaallinksGearchiveerd: number;
    }
  | { ok: false; error: string; code?: string };

/**
 * Roept POST /api/invoices/release-werkorder aan: zet een volledig
 * gecrediteerde werkorder terug op "nog te factureren".
 */
export async function releaseWerkOrderVoorHerfacturatie(
  werkOrderId: string,
  reden: string
): Promise<ReleaseWerkOrderResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/release-werkorder', {
    method: 'POST',
    headers,
    body: JSON.stringify({ werkOrderId, reden }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      error: payload.error || 'Vrijgeven mislukt',
      code: payload.code,
    };
  }
  return {
    ok: true,
    werkOrderNummer: payload.werkOrderNummer ?? null,
    vorigeFactuurNummer: payload.vorigeFactuurNummer ?? null,
    verrekeningBalansGezet: payload.verrekeningBalansGezet ?? 0,
    betaallinksGearchiveerd: payload.betaallinksGearchiveerd ?? 0,
  };
}

// ─── Losse factuur (ad-hoc, zonder werkorder) ────────────────────────────────

export interface StandaloneLineInput {
  omschrijving: string;
  aantal: number;
  /** Prijs per stuk, excl. btw. */
  prijsExclBtw: number;
  btwPct: 0 | 9 | 21;
}

export interface CreateStandaloneBody {
  accountId: string;
  contactId?: string;
  lines: StandaloneLineInput[];
  opmerkingen?: string;
  /** Merk-branding op PDF + e-mail. Losse facturen default 'CINCO'. */
  brand?: 'KLEDA' | 'CINCO';
}

/**
 * Roept POST /api/invoices/create-standalone aan: maakt een losse factuur met
 * vrije regels (omschrijving + aantal + prijs + btw) zonder werkorder.
 * Bron='Office-App' wordt server-side gezet.
 */
export async function createStandaloneInvoice(
  body: CreateStandaloneBody
): Promise<CreateInvoiceResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/create-standalone', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      error: payload.error || 'Factuur aanmaken mislukt',
      code: payload.code,
    };
  }
  return {
    ok: true,
    invoiceId: payload.invoiceId,
    invoiceNaam: payload.invoiceNaam,
    lineItemsCreated: payload.lineItemsCreated ?? 0,
  };
}

// ─── Crediteren ─────────────────────────────────────────────────────────────

export interface CreditLineInput {
  /** Bestaande factuurregel volledig crediteren. */
  lineItemId?: string;
  /** Of een vrij bedrag: omschrijving + bedrag incl. BTW + btw-percentage. */
  omschrijving?: string;
  bedragInclBtw?: number;
  btwPct?: 0 | 9 | 21;
}

export interface CreateCreditBody {
  invoiceId: string;
  /** WO van het origineel — verplicht wanneer refund wordt meegegeven. */
  werkOrderId?: string;
  lines: CreditLineInput[];
  reden: string;
  toelichting?: string;
  refund?: {
    methode: 'Mollie' | 'Bank';
    bedrag: number;
    redenVoorMollie?: string;
  };
  send?: boolean;
  /** Zet de werkorder na een volledige credit terug op "nog te factureren". */
  releaseWerkOrder?: boolean;
}

export interface CreditStepError {
  step: 'refund' | 'send' | 'release';
  code?: string;
  message?: string;
}

export type CreateCreditResult =
  | {
      ok: true;
      creditInvoiceId: string;
      creditInvoiceNaam: string;
      creditTotaalInclBtw: number;
      verrekend: number;
      terugTeBetalen: number;
      origineelOpenstaandNa: number;
      refund: { refundId: string; refundName: string; status: string } | null;
      sent: boolean;
      /** Werkorder staat weer open voor een nieuwe factuur. */
      released: boolean;
      stepErrors: CreditStepError[];
    }
  | { ok: false; error: string; code?: string };

/**
 * Roept POST /api/invoices/credit aan: creditfactuur + verrekening, optionele
 * terugstorting en optioneel versturen. `stepErrors` bevat gefaalde
 * vervolg-stappen (refund/send) terwijl de creditfactuur zelf wél staat.
 */
export async function createCreditInvoice(body: CreateCreditBody): Promise<CreateCreditResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/invoices/credit', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: payload.error || 'Crediteren mislukt', code: payload.code };
  }
  return {
    ok: true,
    creditInvoiceId: payload.creditInvoiceId,
    creditInvoiceNaam: payload.creditInvoiceNaam,
    creditTotaalInclBtw: payload.creditTotaalInclBtw ?? 0,
    verrekend: payload.verrekend ?? 0,
    terugTeBetalen: payload.terugTeBetalen ?? 0,
    origineelOpenstaandNa: payload.origineelOpenstaandNa ?? 0,
    refund: payload.refund ?? null,
    sent: payload.sent === true,
    released: payload.released === true,
    stepErrors: payload.stepErrors ?? [],
  };
}

export type DeleteTransactieResult = { ok: true } | { ok: false; error: string; code?: string };

export async function deleteTransactie(id: string): Promise<DeleteTransactieResult> {
  const headers = await authHeaders();
  const response = await fetch(`/api/transacties/delete?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Verwijderen mislukt' }));
    return { ok: false, error: err.error || 'Verwijderen mislukt', code: err.code };
  }
  return { ok: true };
}
